import useInApp from '@/hooks/useInApp';
import { colorManagerAtom } from '@/store/uiStore';
import { trpc } from '@/utils/trpc';
import { social_links } from '@prisma/client';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import React, { useMemo } from 'react';

const MAX_PROVIDER_LENGTH = 12;
interface LinkHoverButtonsProps {
  onEditClick: () => void;
  link: social_links & { bookmarkId: number };
  currentNoteState?: { span: number; showNote: boolean };
}

export default function LinkHoverButtons({
  onEditClick,
  link,
  currentNoteState,
}: LinkHoverButtonsProps) {
  const [colorManager] = useAtom(colorManagerAtom);
  const { inPublicMoodbard } = useInApp();
  const { data: linkInfo, isLoading: apiIsLoading } = trpc.iframely.getLinkInfo.useQuery(link.url);

  const isUnsupportedLink = useMemo(() => {
    return !linkInfo?.html && !apiIsLoading;
  }, [linkInfo, apiIsLoading]);

  const truncateProvider = useMemo(() => {
    if (link.provider) {
      if (link.provider.length <= MAX_PROVIDER_LENGTH) return link.provider;
      return link.provider.substring(0, MAX_PROVIDER_LENGTH) + '...';
    }
    return '';
  }, [link]);

  return (
    <div className="absolute bottom-[-21px] right-[0px] flex gap-[15px] text-nhg text-[14px] font-[450]">
      <a
        href={linkInfo?.url ?? link.url}
        target="_blank"
        className={clsx(
          [
            'hover:text-orange-400 after:hover:absolute after:hover:bottom-[-1px] after:hover:left-0 after:hover:w-full after:hover:h-px after:hover:bg-orange-400',
          ],
          colorManager.text ?? 'text-white',
          'relative inline-flex items-center gap-2',
          currentNoteState?.showNote ? 'invisible' : 'visible'
        )}
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 13 13"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1.44444 0C0.65501 0 0 0.65501 0 1.44444V11.5556C0 12.345 0.65501 13 1.44444 13H11.5556C12.345 13 13 12.345 13 11.5556V6.5H11.5556V11.5556H1.44444V1.44444H6.5V0H1.44444ZM7.94444 0V1.44444H10.5343L3.8227 8.15603L4.84397 9.1773L11.5556 2.46571V5.05556H13V0H7.94444Z"
            fill="currentColor"
          />
        </svg>
        {link.provider ? (
          <span className="uppercase font-nhg text-[14px] leading-[14px] tracking-[0.15em] font-[400]">
            {truncateProvider}
          </span>
        ) : (
          <span className="font-nhg text-[14px] leading-[14px] tracking-[0.15em] font-[400]">
            LINK
          </span>
        )}
      </a>
      {!inPublicMoodbard && (
        <div
          className={clsx(
            ['hover:text-orange-400'],
            colorManager.text ?? 'text-white',
            'relative inline-flex items-end gap-2',
            currentNoteState?.showNote
              ? 'visible'
              : 'after:hover:absolute after:hover:bottom-[-1px] after:hover:left-0 after:hover:w-full after:hover:h-px after:hover:bg-orange-400'
          )}
          onClick={onEditClick}
        >
          <span
            className={clsx([
              'font-nhg text-[14px] leading-[14px] tracking-[0.15em] font-[400]',
              currentNoteState?.showNote ? 'text-[#A2EE00]' : '',
            ])}
          >
            {isUnsupportedLink ? 'FIX LINK' : 'EDIT'}
          </span>
        </div>
      )}
    </div>
  );
}
